import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable, useGlobalFilter, useFilters, usePagination, useSortBy } from 'react-table';
import { useParams, Link, useHistory } from 'react-router-dom';
import axios from '../../../../../services/AxiosInstance';
import NewCustomerModal from './NewCustomerModal';
import PageHeader from '../../../../layouts/PageHeader';
import { ListToolbar, FilterChips } from '../../../Common/ListToolbar';
import { EmptyState } from '../../../Common/EmptyState';
import { Users, Trash2 } from 'lucide-react';
import { useListControls } from '../../../../../hooks/useListControls';
import { NoData } from '../../../SharedComponents/NoData';
import { Dropdown, Spinner, SplitButton, Button, Alert } from 'react-bootstrap';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import swal from 'sweetalert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faUpload,
  faSync,
  faSpinner,
  faFileExport
} from '@fortawesome/free-solid-svg-icons';

export const CustomersTable = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pageSize, setPageSize] = useState(10);

  // Memoize handlers
  const handleClose = useCallback(() => {
    setShow(false);
    setSelectedCustomer(null);
  }, []);

  const handleDeleteCustomer = useCallback(async (customerId) => {
    const result = await swal({
      title: "Är du säker?",
      text: "Den här åtgärden kan inte ångras!",
      icon: "warning",
      buttons: ["Avbryt", "Ta bort"],
      dangerMode: true,
    });

    if (result) {
      try {
        const deleteCustomerApiUrl = getApiUrl(`${companyId}/sales/customers/${customerId}`);
        await axios.delete(deleteCustomerApiUrl, { withCredentials: true });
        await fetchCustomers();
        swal('Borttagen!', 'Kunden har tagits bort.', 'success');
      } catch (error) {
        swal('Fel', 'Kunde inte ta bort kunden', 'error');
        console.error(error);
      }
    }
  }, [companyId]);


  const saveNewCustomer = async (newData) => {
    try {
      const newCustomer = {
        type: newData.type,
        orgnumber: newData.orgnumber,
        vatnumber: newData.vatnumber,
        name: newData.name || '',
        first_name: newData.first_name || '',
        last_name: newData.last_name || '',
        phone: newData.phone || '',
        payment_terms: newData.payment_terms || '',
        addresses: newData.addresses || [],
        contacts: newData.contacts || [],
      };

      const customersApiUrl = getApiUrl(`${companyId}/sales/customers`);
      await axios.post(customersApiUrl, newCustomer, { withCredentials: true });
      await fetchCustomers();
      swal('Framgång!', 'Kunden har skapats.', 'success');
    } catch (error) {
      swal('Fel', 'Kunde inte skapa kund', 'error');
      console.error(error);
    }
  };

  const handleUpdateCustomer = async (updatedCustomerData) => {
    try {
      const updateCustomerApiUrl = getApiUrl(`${companyId}/sales/customers/${updatedCustomerData.id}`);
      await axios.put(updateCustomerApiUrl, updatedCustomerData, { withCredentials: true });
      await fetchCustomers();
      swal('Uppdaterad!', 'Kunden har uppdaterats.', 'success');
    } catch (error) {
      swal('Fel', 'Kunde inte uppdatera kund', 'error');
      console.error(error);
    }
  };

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const customersApiUrl = getApiUrl(`${companyId}/sales/customers`);
      const response = await axios.get(customersApiUrl, { withCredentials: true });
      setCustomers(response.data);
    } catch (error) {
      setError('Kunde inte hämta kunder. Försök igen senare.');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchCustomers();
  };

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const CUSTOMERS_COLUMNS = useMemo(() => [
    {
      Header: 'Kundnummer',
      accessor: 'number',
      Cell: ({ value }) => (
        <span style={{
          fontSize: '0.875rem',
          color: '#6b7280',
          fontVariantNumeric: 'tabular-nums'
        }}>
          {value || '-'}
        </span>
      ),
    },
    {
      Header: 'Namn',
      accessor: 'name',
      Cell: ({ value, row }) => (
        <div>
          <div>
            <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
              {value || '-'}
            </div>
            {row.original.orgnumber && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {row.original.orgnumber}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      Header: 'Typ',
      accessor: 'type',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem' }}>
          {value === 'company' ? 'Företag' : value === 'individual' ? 'Privat' : value}
        </span>
      ),
    },
    {
      Header: 'Kontakt',
      accessor: 'email',
      Cell: ({ value, row }) => (
        <div style={{ fontSize: '0.875rem' }}>
          {value && (
            <div className="text-truncate" style={{ maxWidth: '200px' }}>
              {value}
            </div>
          )}
          {row.original.phone && (
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {row.original.phone}
            </div>
          )}
          {!value && !row.original.phone && '-'}
        </div>
      ),
    },
    {
      Header: '',
      className: 'column-right',
      Cell: ({ row }) => (
        <div className="ll-row-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="is-danger"
            onClick={() => handleDeleteCustomer(row.original.id)}
            title="Ta bort"
            aria-label="Ta bort"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
      id: 'actions',
    },
  ], [handleDeleteCustomer]);

  const { search, setSearch, debouncedSearch, filter: typeFilter, setFilter: setTypeFilter } =
    useListControls({ defaultFilter: 'all', defaultSort: null, filterKey: 'type' });

  const typeCounts = useMemo(() => ({
    all: customers.length,
    company: customers.filter((c) => c.type === 'company').length,
    individual: customers.filter((c) => c.type === 'individual').length,
  }), [customers]);

  const visibleCustomers = useMemo(
    () => (typeFilter === 'all' ? customers : customers.filter((c) => c.type === typeFilter)),
    [customers, typeFilter],
  );

  const tableInstance = useTable(
    {
      columns: CUSTOMERS_COLUMNS,
      data: visibleCustomers,
      initialState: { pageSize },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize: setTablePageSize,
    setGlobalFilter,
  } = tableInstance;

  const { pageIndex } = state;
  // Search lives in the URL; react-table's own globalFilter is driven
  // from the debounced value so a filtered table is linkable.
  useEffect(() => { setGlobalFilter(debouncedSearch || undefined); },
    [debouncedSearch, setGlobalFilter]);

  // Enhanced pagination controls
  const PaginationControls = () => (
    <div
      style={{
        backgroundColor: '#fff',
        borderTop: '1px solid #f1f5f9',
        padding: '1rem 1.5rem'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm"
            style={{
              width: 'auto',
              fontSize: '0.875rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }}
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setTablePageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map(size => (
              <option key={size} value={size}>{size} per sida</option>
            ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
            Visar {page.length} av {customers.length} kunder
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'<<'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem' }}
          >
            Föregående
          </Button>
          <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
            Sida {pageIndex + 1} av {pageOptions.length}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem' }}
          >
            Nästa
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'>>'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
  
  .table td {
    vertical-align: middle !important;
  }
  
  .clickable-row {
    cursor: pointer;
  }
  
  .clickable-row:hover {
    background-color: #f8f9fa;
  }
  
  /* Ensure dropdown button is visible */
  .btn-outline-secondary {
    border-color: #6c757d !important;
  }
  
  .btn-outline-secondary:hover {
    background-color: #6c757d !important;
    border-color: #6c757d !important;
    color: #fff !important;
  }
  .split-button-custom .dropdown-toggle::after {
    display: inline-block !important;
    margin-left: 0.255em;
    vertical-align: 0.255em;
    content: "";
    border-top: 0.3em solid;
    border-right: 0.3em solid transparent;
    border-bottom: 0;
    border-left: 0.3em solid transparent;
  }
  
  .split-button-custom .dropdown-toggle {
    border-left: 1px solid rgba(255, 255, 255, 0.2);
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
`}</style>

      <PageHeader
        icon={Users}
        title="Kunder"
        subtitle="Dina kunder och deras uppgifter."
      />
      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

            {/* Enhanced header with actions */}
            <div className="d-flex align-items-center justify-content-end mb-4">
              <SplitButton
                id="customer-actions"
                title={
                  <span className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Ny kund
                  </span>
                }
                variant="primary"
                onClick={() => setShow(true)}
                className="split-button-custom"
              >
                <Dropdown.Item onClick={() => history.push(`/${companyId}/customers/import`)}>
                  <FontAwesomeIcon icon={faUpload} className="me-2" />
                  Importera kunder
                </Dropdown.Item>
                {/*<Dropdown.Item onClick={() => alert("Exportfunktion kommer snart")}>
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  Exportera kunder
                </Dropdown.Item>*/}
              </SplitButton>
            </div>

            {refreshing && (
              <div className="text-center mb-3">
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                Uppdaterar kundlista...
              </div>
            )}

            <ListToolbar
              className="mb-3"
              search={{
                value: search,
                onChange: setSearch,
                placeholder: 'Sök kund, org.nr, e-post…',
                ariaLabel: 'Sök bland kunder',
              }}
            />

            <FilterChips
              className="mb-3"
              chips={[
                { key: 'all', label: 'Alla', count: typeCounts.all },
                { key: 'company', label: 'Företag', count: typeCounts.company },
                { key: 'individual', label: 'Privatpersoner', count: typeCounts.individual },
              ]}
              value={typeFilter}
              onChange={setTypeFilter}
            />

            {customers.length === 0 && !loading ? (
                  <EmptyState
                    icon={Users}
                    title="Inga kunder ännu"
                    description="Lägg till din första kund — sedan kan du fakturera dem direkt härifrån."
                    actions={
                      <>
                        <Button variant="primary" onClick={() => setShow(true)}>Skapa ny kund</Button>
                        <Button variant="outline-secondary" onClick={() => history.push(`/${companyId}/customers/import`)}>Importera kunder</Button>
                      </>
                    }
                  />
            ) : (
              <div className="ll-table-card">
                <div>

                  <div className="table-responsive">
                    <table {...getTableProps()} className="ll-table ll-table--clickable">
                      <thead>
                        {headerGroups.map((headerGroup, headerGroupIndex) => (
                          <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroupIndex}`}>
                            {headerGroup.headers.map((column, columnIndex) => (
                              <th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                key={`header-${columnIndex}-${column.id}`}
                                style={{ cursor: column.canSort ? 'pointer' : 'default' }}
                              >
                                <div className="d-flex align-items-center justify-content-between">
                                  <span>{column.render('Header')}</span>
                                  {column.canSort && (
                                    <span className={`ll-table__sort ${column.isSorted ? 'active' : ''}`}>
                                      {column.isSorted ? (column.isSortedDesc ? '↓' : '↑') : '↕'}
                                    </span>
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                          prepareRow(row);
                          return (
                            <tr
                              {...row.getRowProps()}
                              key={row.id}
                              onClick={() => history.push(`/${companyId}/customers/${row.original.id}`)}
                            >
                              {row.cells.map(cell => (
                                <td
                                  {...cell.getCellProps()}
                                  key={cell.column.id}
                                  className={cell.column.id === 'actions' ? 'll-table__actions' : undefined}
                                >
                                  {cell.column.id === 'actions' ? cell.render('Cell') : (cell.value !== null && cell.value !== undefined ? cell.render('Cell') : '-')}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                </div>

                  <PaginationControls />
                </div>
              </div>
            )}

        <NewCustomerModal
          show={show}
          handleClose={handleClose}
          handleSave={(data) => {
            saveNewCustomer(data);
            handleClose();
          }}
          handleUpdate={(data) => {
            handleUpdateCustomer(data);
            handleClose();
          }}
          customerDetails={selectedCustomer}
        />
      </div>
    </>
  );
};

export default CustomersTable;



// import React, { useMemo, useState, useEffect, useCallback } from 'react';
// import { useTable, useGlobalFilter, useFilters, usePagination } from 'react-table';
// import { useParams, Link, useHistory } from 'react-router-dom';
// import axios from '../../../../../services/AxiosInstance';
// // import NewCustomerModal from './NewCustomerModal';
// import PageHeader from '../../../../layouts/PageHeader';
// import { NoData } from '../../../SharedComponents/NoData';
// import { Dropdown, Spinner, SplitButton } from 'react-bootstrap';
// import { getApiUrl } from '../../../../../utils/ApiUtils';
// import swal from 'sweetalert';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faPlus, faUpload } from '@fortawesome/free-solid-svg-icons';

// export const CustomersTable = () => {
//   const { companyId } = useParams();
//   const history = useHistory();
//   const [customers, setCustomers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [show, setShow] = useState(false);
//   const [selectedCustomer, setSelectedCustomer] = useState(null);
//   const [pageSize, setPageSize] = useState(10);

//   // Memoize handlers
//   const handleClose = useCallback(() => {
//     setShow(false);
//     setSelectedCustomer(null);
//   }, []);

//   const handleDeleteCustomer = useCallback(async (customerId) => {
//     try {
//       const deleteCustomerApiUrl = getApiUrl(`${companyId}/sales/customers/${customerId}`);
//       await axios.delete(deleteCustomerApiUrl, { withCredentials: true });
//       fetchCustomers();
//     } catch (error) {
//       swal('Fel', 'Kunde inte ta bort kunden', 'error');
//       console.error(error);
//     }
//   }, [companyId]);

//   const handleEditCustomer = useCallback(async (customer) => {
//     try {
//       const getCustomerApiUrl = getApiUrl(`${companyId}/sales/customers/${customer.id}`);
//       const response = await axios.get(getCustomerApiUrl, { withCredentials: true });
//       setSelectedCustomer(response.data);
//       setShow(true);
//     } catch (error) {
//       swal('Fel', 'Kunde inte hämta kunddata', 'error');
//       console.error(error);
//     }
//   }, [companyId]);

//   const saveNewCustomer = async (newData) => {
//     try {
//       const newCustomer = {
//         type: newData.type,
//         orgnumber: newData.orgnumber,
//         vatnumber: newData.vatnumber,
//         name: newData.name || '',
//         first_name: newData.first_name || '',
//         last_name: newData.last_name || '',
//         phone: newData.phone || '',
//         payment_terms: newData.payment_terms || '',
//         addresses: newData.addresses || [],
//         contacts: newData.contacts || [],
//       };

//       const customersApiUrl = getApiUrl(`${companyId}/sales/customers`);
//       await axios.post(customersApiUrl, newCustomer, { withCredentials: true });
//       await fetchCustomers();
//     } catch (error) {
//       swal('Fel', 'Kunde inte skapa kund', 'error');
//       console.error(error);
//     }
//   };

//   const handleUpdateCustomer = async (updatedCustomerData) => {
//     try {
//       const updateCustomerApiUrl = getApiUrl(`${companyId}/sales/customers/${updatedCustomerData.id}`);
//       await axios.put(updateCustomerApiUrl, updatedCustomerData, { withCredentials: true });
//       await fetchCustomers();
//     } catch (error) {
//       swal('Fel', 'Kunde inte uppdatera kund', 'error');
//       console.error(error);
//     }
//   };

//   const fetchCustomers = useCallback(async () => {
//     setLoading(true);
//     try {
//       const customersApiUrl = getApiUrl(`${companyId}/sales/customers`);
//       const response = await axios.get(customersApiUrl, { withCredentials: true });
//       setCustomers(response.data);
//     } catch (error) {
//       setError(error);
//       swal('Fel', 'Kunde inte hämta kunder', 'error');
//     } finally {
//       setLoading(false);
//     }
//   }, [companyId]);

//   useEffect(() => {
//     fetchCustomers();
//   }, [fetchCustomers]);

//   const CUSTOMERS_COLUMNS = useMemo(() => [
//     {
//       Header: 'Kundnummer',
//       accessor: 'number',
//     },
//     {
//       Header: 'Namn',
//       accessor: 'name',
//     },
//     {
//       Header: 'Typ',
//       accessor: 'type',
//       Cell: ({ value }) => value === 'company' ? 'Företag' : value === 'individual' ? 'Privat' : value,
//     },
//     {
//       Header: '',
//       className: 'column-right',
//       Cell: ({ row }) => (
//         <Dropdown className="dropdown ms-auto text-right" onClick={(e) => e.stopPropagation()}>
//           <Dropdown.Toggle variant="" className="btn-link i-false border-0 p-0">
//             <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
//               <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
//                 <rect x={0} y={0} width={24} height={24} />
//                 <circle fill="#000000" cx={5} cy={12} r={2} />
//                 <circle fill="#000000" cx={12} cy={12} r={2} />
//                 <circle fill="#000000" cx={19} cy={12} r={2} />
//               </g>
//             </svg>
//           </Dropdown.Toggle>
//           <Dropdown.Menu className="dropdown-menu dropdown-menu-right">
//             <Dropdown.Item onClick={() => handleEditCustomer(row.original)}>
//               Redigera kund
//             </Dropdown.Item>
//             <Dropdown.Item onClick={() => handleDeleteCustomer(row.original.id)}>
//               Ta bort kund
//             </Dropdown.Item>
//           </Dropdown.Menu>
//         </Dropdown>
//       ),
//       id: 'actions',
//     },
//   ], [handleDeleteCustomer, handleEditCustomer]);

//   const tableInstance = useTable(
//     {
//       columns: CUSTOMERS_COLUMNS,
//       data: customers,
//       initialState: { pageSize },
//     },
//     useFilters,
//     useGlobalFilter,
//     usePagination
//   );

//   const {
//     getTableProps,
//     getTableBodyProps,
//     headerGroups,
//     prepareRow,
//     state,
//     page,
//     nextPage,
//     previousPage,
//     canNextPage,
//     canPreviousPage,
//     pageOptions,
//     gotoPage,
//     pageCount,
//     setPageSize: setTablePageSize,
//     setGlobalFilter,
//   } = tableInstance;

//   const { globalFilter, pageIndex } = state;

//   // Pagination controls component
//   const PaginationControls = () => (
//     <div className="d-flex justify-content-between align-items-center mt-3">
//       <select
//         className="form-select form-select-sm"
//         style={{ width: 'auto' }}
//         value={pageSize}
//         onChange={e => {
//           setPageSize(Number(e.target.value));
//           setTablePageSize(Number(e.target.value));
//         }}
//       >
//         {[10, 20, 30, 40, 50].map(size => (
//           <option key={size} value={size}>{size} per sida</option>
//         ))}
//       </select>
//       <div className="d-flex align-items-center gap-3">
//         <a
//           href="#"
//           className={`text-decoration-none ${!canPreviousPage ? 'text-muted' : 'text-primary'}`}
//           onClick={(e) => {
//             e.preventDefault();
//             if (canPreviousPage) gotoPage(0);
//           }}
//         >
//           {'<<'}
//         </a>
//         <a
//           href="#"
//           className={`text-decoration-none ${!canPreviousPage ? 'text-muted' : 'text-primary'}`}
//           onClick={(e) => {
//             e.preventDefault();
//             if (canPreviousPage) previousPage();
//           }}
//         >
//           Föregående
//         </a>
//         <small className="text-muted">
//           Sida {pageIndex + 1} av {pageOptions.length}
//         </small>
//         <a
//           href="#"
//           className={`text-decoration-none ${!canNextPage ? 'text-muted' : 'text-primary'}`}
//           onClick={(e) => {
//             e.preventDefault();
//             if (canNextPage) nextPage();
//           }}
//         >
//           Nästa
//         </a>
//         <a
//           href="#"
//           className={`text-decoration-none ${!canNextPage ? 'text-muted' : 'text-primary'}`}
//           onClick={(e) => {
//             e.preventDefault();
//             if (canNextPage) gotoPage(pageCount - 1);
//           }}
//         >
//           {'>>'}
//         </a>
//       </div>
//     </div>
//   );

//   if (loading) {
//     return (
//       <div className="vh-100 d-flex justify-content-center align-items-center">
//         <Spinner animation="border" role="status" className="me-2" />
//         <div>Laddar...</div>
//       </div>
//     );
//   }

//   if (error) {
//     return <div className="alert alert-danger">Fel vid laddning av kunder</div>;
//   }

//   return (
//     <>
//       <PageTitle activeMenu="Kunder" motherMenu="Fakturor och Offerter" />
//       <div className="col-12">
//         {customers.length === 0 ? (
//           <>
//             <NoData message="Det finns inga kunder att visa. Skapa en för att kunna se dem här." />
//             <div className="d-flex justify-content-center gap-3 p-3">
//               <button onClick={() => setShow(true)} className="btn btn-primary">
//                 <FontAwesomeIcon icon={faPlus} className="me-2" />
//                 Skapa en ny kund
//               </button>
//               <button onClick={() => history.push(`/${companyId}/customers/import`)} className="btn btn-outline-secondary">
//                 <FontAwesomeIcon icon={faUpload} className="me-2" />
//                 Importera Kunder
//               </button>
//             </div>
//           </>
//         ) : (
//           <div className="card">
//             <div className="card-header d-flex justify-content-between align-items-center">
//               <h4 className="card-title mb-0">Kunder</h4>
//               <SplitButton
//                 id="dropdown-split-variants-primary"
//                 title="Ny Kund"
//                 variant="primary"
//                 onClick={() => setShow(true)}
//               >
//                 <Dropdown.Item onClick={() => history.push(`/${companyId}/customers/import`)}>
//                   Importera Kunder
//                 </Dropdown.Item>
//                 <Dropdown.Item onClick={() => alert("inte tillagd")}>
//                   Exportera Kunder
//                 </Dropdown.Item>
//               </SplitButton>
//             </div>
//             <div className="card-body">
//               <div className="table-responsive">
//                 <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
//                 <table {...getTableProps()} className="table dataTable table-hover">
//                   <thead>
//                     {headerGroups.map(headerGroup => (
//                       <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
//                         {headerGroup.headers.map(column => (
//                           <th {...column.getHeaderProps()} key={column.id}>
//                             {column.render('Header')}
//                           </th>
//                         ))}
//                       </tr>
//                     ))}
//                   </thead>
//                   <tbody {...getTableBodyProps()}>
//                     {page.map(row => {
//                       prepareRow(row);
//                       return (
//                         <tr
//                           {...row.getRowProps()}
//                           key={row.id}
//                           className="clickable-row"
//                           onClick={() => history.push(`/${companyId}/customers/${row.original.id}`)}
//                         >
//                           {row.cells.map(cell => (
//                             <td {...cell.getCellProps()} key={cell.column.id}>
//                               {cell.value !== null ? cell.render('Cell') : '-'}
//                             </td>
//                           ))}
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//                 <PaginationControls />
//               </div>
//             </div>
//           </div>
//         )}
//         <NewCustomerModal
//           show={show}
//           handleClose={handleClose}
//           handleSave={(data) => {
//             saveNewCustomer(data);
//             handleClose();
//           }}
//           handleUpdate={(data) => {
//             handleUpdateCustomer(data);
//             handleClose();
//           }}
//           customerDetails={selectedCustomer}
//         />
//       </div>
//     </>
//   );
// };

// export default CustomersTable;